import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Footer from '../components/Footer';
import OnboardingTour from '../components/OnboardingTour';
import HelpModal from '../components/HelpModal';
import { helpGuides } from '../utils/helpGuides';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface Course {
  id: string;
  title: string;
  description: string;
  niche: string;
  status: 'draft' | 'published';
  createdAt: string;
  modules?: any[];
}

const Dashboard: React.FC = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewCourseModal, setShowNewCourseModal] = useState(false);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    niche: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCourses();

    // Check if user has seen onboarding
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    if (!hasSeenOnboarding) {
      // Show onboarding after a brief delay
      setTimeout(() => setShowOnboarding(true), 1000);
    }

    // Check for payment status in URL params
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast.success('Payment successful! Your account has been upgraded.');
      // Clear the query param
      window.history.replaceState({}, '', '/dashboard');
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. You can upgrade anytime from your dashboard.');
      // Clear the query param
      window.history.replaceState({}, '', '/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCourses(response.data.courses);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(
        `${API_URL}/courses`,
        newCourse,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setCourses([...courses, response.data.course]);
      setShowNewCourseModal(false);
      setNewCourse({ title: '', description: '', niche: '' });
    } catch (error: any) {
      console.error('Failed to create course:', error);
      alert(error.response?.data?.error || 'Failed to create course');
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowOnboarding(false);
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'premium':
        return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-yellow-900';
      case 'basic':
        return 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Onboarding Tour */}
      {showOnboarding && <OnboardingTour onComplete={handleOnboardingComplete} />}

      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4 md:space-x-8">
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                  The Course Creator's Vault
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getTierBadgeColor(user?.tier || 'free')}`}>
                  {user?.tier?.toUpperCase()}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-900 font-medium text-sm"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Mobile/Desktop Navigation Tabs */}
          <div className="border-t border-gray-200 -mb-px">
            <nav className="flex overflow-x-auto space-x-4 sm:space-x-6 py-2 px-1">
              <button onClick={() => navigate('/dashboard')} className="text-gray-900 font-semibold whitespace-nowrap text-sm sm:text-base pb-2 border-b-2 border-indigo-600">Dashboard</button>
              <button onClick={() => navigate('/templates')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Templates</button>
              <button onClick={() => navigate('/brand-settings')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Brand</button>
              <button onClick={() => navigate('/creation-stats')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Stats</button>
              <button onClick={() => navigate('/pricing')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Pricing</button>
              <button onClick={() => navigate('/help')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Help</button>
              <button onClick={() => navigate('/contact')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Contact</button>
            </nav>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome back, {user?.name}!
            </h2>
            <p className="text-gray-600">
              Let's create something amazing today
            </p>
          </div>
          <button
            onClick={() => setShowHelpModal(true)}
            className="px-4 py-2 bg-purple-100 text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-200 flex items-center gap-2 font-medium"
            title="Dashboard help and tour"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Help
          </button>
        </div>

        {/* Free Tier Course Counter */}
        {user?.tier === 'free' && (
          <div className={`mb-6 rounded-xl shadow-lg p-6 border-2 ${
            courses.length >= 3
              ? 'bg-red-50 border-red-300'
              : courses.length === 2
                ? 'bg-yellow-50 border-yellow-300'
                : 'bg-blue-50 border-blue-300'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`text-4xl ${
                  courses.length >= 3 ? 'animate-bounce' : ''
                }`}>
                  {courses.length >= 3 ? '' : courses.length === 2 ? '' : ''}
                </div>
                <div>
                  <h4 className={`text-lg font-bold ${
                    courses.length >= 3
                      ? 'text-red-900'
                      : courses.length === 2
                        ? 'text-yellow-900'
                        : 'text-blue-900'
                  }`}>
                    {courses.length >= 3
                      ? 'No Free AI Courses Left!'
                      : `Free AI Courses: ${courses.length}/3 Used`
                    }
                  </h4>
                  <p className={`text-sm ${
                    courses.length >= 3
                      ? 'text-red-700'
                      : courses.length === 2
                        ? 'text-yellow-700'
                        : 'text-blue-700'
                  }`}>
                    {courses.length >= 3
                      ? 'You\'ve used all 3 free AI-generated courses. Upgrade to keep creating!'
                      : courses.length === 2
                        ? 'You\'re almost out of credits! Only 1 free AI course remaining.'
                        : `You have ${3 - courses.length} free AI-generated course${3 - courses.length === 1 ? '' : 's'} remaining.`
                    }
                  </p>
                </div>
              </div>
              {courses.length >= 2 && (
                <button
                  onClick={() => navigate('/pricing')}
                  className={`px-6 py-2 rounded-lg font-semibold transition ${
                    courses.length >= 3
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-yellow-600 text-white hover:bg-yellow-700'
                  }`}
                >
                  Upgrade Now
                </button>
              )}
            </div>
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    courses.length >= 3
                      ? 'bg-red-600'
                      : courses.length === 2
                        ? 'bg-yellow-500'
                        : 'bg-blue-500'
                  }`}
                  style={{ width: `${(courses.length / 3) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Banner for Free Users */}
        {user?.tier === 'free' && showUpgradeBanner && (
          <div className="mb-8 bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden">
            <button
              onClick={() => setShowUpgradeBanner(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-200 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-8">
                <h3 className="text-2xl font-bold mb-2">Unlock Unlimited Potential</h3>
                <p className="text-white text-opacity-90 mb-4">
                  Upgrade to Basic or Premium to create unlimited AI-generated courses, access advanced marketing tools, custom branding, and more!
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate('/pricing')}
                    className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:shadow-xl transform hover:scale-105 transition"
                  >
                    View Pricing Plans
                  </button>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="text-6xl">*</div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Courses</p>
                <p className="text-3xl font-bold text-gray-900">{courses.length}</p>
              </div>
              <div className="bg-indigo-100 p-3 rounded-lg">
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Published</p>
                <p className="text-3xl font-bold text-gray-900">
                  {courses.filter(c => c.status === 'published').length}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Drafts</p>
                <p className="text-3xl font-bold text-gray-900">
                  {courses.filter(c => c.status === 'draft').length}
                </p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Marketing & Automation Tools */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Marketing & Automation Tools</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Email Drip Campaigns */}
            <button
              onClick={() => navigate('/email-sequence')}
              className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Email Sequences</h4>
              <p className="text-sm text-indigo-100">AI-powered email campaigns</p>
            </button>

            {/* Social Calendar */}
            <button
              onClick={() => navigate('/social-calendar')}
              className="bg-gradient-to-br from-pink-500 to-rose-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Social Calendar</h4>
              <p className="text-sm text-pink-100">Plan your content schedule</p>
            </button>

            {/* Brand Settings */}
            <button
              onClick={() => navigate('/brand-settings')}
              className="bg-gradient-to-br from-amber-500 to-orange-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Brand Settings</h4>
              <p className="text-sm text-amber-100">Customize your brand colors & fonts</p>
            </button>

            {/* Email Templates */}
            <button
              onClick={() => navigate('/email-templates')}
              className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Email Templates</h4>
              <p className="text-sm text-cyan-100">Design beautiful emails</p>
            </button>

            {/* Certificates */}
            <button
              onClick={() => navigate('/certificates')}
              className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Certificates</h4>
              <p className="text-sm text-purple-100">Generate completion certificates</p>
            </button>

            {/* Integrations */}
            <button
              onClick={() => navigate('/integrations')}
              className="bg-gradient-to-br from-emerald-500 to-green-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Integrations</h4>
              <p className="text-sm text-emerald-100">Connect your tools</p>
            </button>

            {/* Video Generator */}
            <button
              onClick={() => navigate('/video-generator')}
              className="bg-gradient-to-br from-red-500 to-orange-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">AI Video Generator</h4>
              <p className="text-sm text-red-100">Create videos with AI</p>
            </button>

            {/* Creation Stats */}
            <button
              onClick={() => navigate('/creation-stats')}
              className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Creation Stats</h4>
              <p className="text-sm text-teal-100">Track your progress</p>
            </button>

            {/* Freebie Vault */}
            <button
              onClick={() => navigate('/freebies')}
              className="bg-gradient-to-br from-violet-500 to-purple-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Freebie Vault</h4>
              <p className="text-sm text-violet-100">Download templates & guides</p>
            </button>

            {/* Drip Campaigns */}
            <button
              onClick={() => navigate('/drip-campaigns')}
              className="bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Drip Campaigns</h4>
              <p className="text-sm text-fuchsia-100">Automated email sequences</p>
            </button>

            {/* Sales Funnels */}
            <button
              onClick={() => navigate('/sales-funnels')}
              className="bg-gradient-to-br from-rose-500 to-red-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
            >
              <div className="text-3xl mb-2">*</div>
              <h4 className="text-lg font-bold mb-1">Sales Funnels</h4>
              <p className="text-sm text-rose-100">6 proven funnel templates</p>
            </button>

            {/* Admin Panel (if admin) */}
            {(user?.email === 'sloane@templimizeai.com' || user?.email === 'ashlen.moody@gmail.com') && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-gradient-to-br from-red-500 to-pink-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-left"
              >
                <div className="text-3xl mb-2">*</div>
                <h4 className="text-lg font-bold mb-1">Admin Panel</h4>
                <p className="text-sm text-red-100">Manage platform</p>
              </button>
            )}
          </div>
        </div>

        {/* Courses Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-3">
            <h3 className="text-xl font-bold text-gray-900">My Courses</h3>
            <div className="flex flex-wrap gap-2 md:gap-3">
              <button
                onClick={() => navigate('/create-course')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-2 md:px-6 rounded-lg text-sm md:text-base font-semibold hover:shadow-lg transform hover:scale-105 transition flex items-center gap-2"
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Create with AI
              </button>
              <button
                onClick={() => navigate('/templates')}
                className="border-2 border-indigo-600 text-indigo-600 px-3 py-2 md:px-6 rounded-lg text-sm md:text-base font-semibold hover:bg-indigo-50 transition"
              >
                Browse Templates
              </button>
              <button
                onClick={() => setShowNewCourseModal(true)}
                className="border-2 border-purple-600 text-purple-600 px-3 py-2 md:px-6 rounded-lg text-sm md:text-base font-semibold hover:bg-purple-50 transition"
              >
                + Manual
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">Loading courses...</p>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">*</div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">No courses yet</h4>
              <p className="text-gray-600 mb-6">Create your first course with AI or manually!</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <button
                  onClick={() => navigate('/create-course')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition"
                >
                  Create with AI
                </button>
                <button
                  onClick={() => navigate('/templates')}
                  className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition"
                >
                  Browse Templates
                </button>
                <button
                  onClick={() => setShowNewCourseModal(true)}
                  className="border-2 border-purple-600 text-purple-600 px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-purple-50 transition"
                >
                  Create Manually
                </button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <div
                  key={course.id}
                  onClick={() => navigate(`/course/${course.id}`)}
                  className="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-500 hover:shadow-lg transition cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      course.status === 'published'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {course.status}
                    </span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{course.title}</h4>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {course.description || 'No description'}
                  </p>
                  {course.niche && (
                    <p className="text-xs text-purple-600 font-semibold mb-4">
                      {course.niche}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{new Date(course.createdAt).toLocaleDateString()}</span>
                    <span className="text-purple-600 hover:text-purple-700 font-semibold">
                      View Course
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Course Modal */}
      {showNewCourseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Create New Course</h3>

            <form onSubmit={handleCreateCourse} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Title *
                </label>
                <input
                  type="text"
                  required
                  value={newCourse.title}
                  onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., Master Digital Marketing"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Niche
                </label>
                <input
                  type="text"
                  value={newCourse.niche}
                  onChange={(e) => setNewCourse({ ...newCourse, niche: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., Marketing, Programming, Fitness"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newCourse.description}
                  onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  rows={3}
                  placeholder="Brief description of your course"
                />
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowNewCourseModal(false)}
                  className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:border-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title={helpGuides.dashboard.title}
        steps={helpGuides.dashboard.steps}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Dashboard;
